import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import {
  CustomTableFilterBar,
  DateFilterTemplate,
  ETypes,
  RColumn,
  RemoteTable,
  RemoteTableProps,
  comboboxFilterTemplate,
  multiSelectFilterTemplate,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import ActiveFilters, { ActiveFilterChip } from '../../../components/novarium/ActiveFilters'
import { formatNumberRange, numberRangeFilterTemplate } from '../../../components/novarium/NumberRangeFilter'
import NvTooltips from '../../../components/novarium/NvTooltips'
import { EMPTY_VALUE, formatIsoDay, formatIsoMoment, textOrDash } from '../../../components/summary/summaryFormat'
import { clearPersistedTableState } from '../../../components/summary/summaryTable'
import useNotify from '../../../hooks/useNotify'
import { UrgencyDetailRowDTO, UrgencyExportFormat, UrgencyPriorityOptionDTO, UrgencySexOptionDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import {
  UrgencyDetailFilters,
  UrgencyTableParams,
  errorMessageOf,
  exportUrgencyDetail,
  getUrgencyDetailRows,
  isRequestCanceled,
} from '../../../services/UrgencySummaryController'
import { readablePriority } from '../../ServicesStatistics/periodDetail'
import { UrgencyDetailRouteState } from '../urgencyDetailState'
import { URGENCY_STATE_GROUP_ID, URGENCY_STATE_MESSAGE_ID } from '../urgencyStateGroups'
import { formatCount, formatMinutes } from '../urgencySummaryFormat'

/** Clave con la que RemoteTable guarda filtros y orden en el navegador. */
const TABLE_ID = 'UrgencyDetailTable'

/**
 * Tipo de dato de cada columna con filtro: define cómo viaja el filtro al backend. Texto: "contiene". Fecha: desde /
 * hasta. Espera: rango en minutos (desde / hasta). Edad: rango en años enteros (el backend rechaza decimales) sobre `ageYears`,
 * aunque la columna muestre `ageText`. Sexo: igualdad por la letra de la opción (`F`, `M`…), no por el nombre. Estado y código de
 * triaje: una lista de opciones (ids), no el texto que muestra la columna.
 */
const FILTERS_TYPE = [
  { field: 'patientName', type: ETypes.string },
  { field: 'affiliateNumber', type: ETypes.string },
  { field: 'ageYears', type: ETypes.integer },
  { field: 'sex', type: ETypes.string, matchMode: 'EQUALS' as const },
  { field: 'guardia', type: ETypes.string },
  { field: 'triageCode', type: ETypes.integer },
  { field: 'notes', type: ETypes.string },
  { field: 'financierName', type: ETypes.string },
  { field: 'stateName', type: ETypes.integer },
  { field: 'evolutionProfessional', type: ETypes.string },
  { field: 'arrivalTime', type: ETypes.dateTime },
  { field: 'waitMinutes', type: ETypes.bigDecimal },
]

/** Nombre de las filas «sin financiador» y «sin profesional», como las da la base. */
const NO_DATA = 'N/D'

/** Lo que acota el detalle y no es un filtro de la tabla: se ve como chip. */
type DetailScope = 'financier' | 'professional'

/** Qué se pone en cada criterio cuando se quita su chip. */
const SCOPE_RESET: Record<DetailScope, Partial<UrgencyDetailFilters>> = {
  financier: { financierId: null, noFinancier: false },
  professional: { noProfessional: false },
}

type ExportItems = NonNullable<RemoteTableProps['exportItems']>

/** Texto de la opción elegida de un filtro de lista (el chip muestra el nombre, no la letra). */
function optionName(options: UrgencySexOptionDTO[]): (id: unknown) => string {
  const names = new Map(options.map((option) => [option.id, option.name]))
  return (id) => (typeof id === 'string' ? names.get(id) ?? '' : '')
}

/** Una opción de un filtro de lista de la tabla. */
interface ListOption {
  id: number
  name: string
}

/** Texto de cada opción elegida de un filtro de lista de ids (el chip muestra los nombres, no los números). */
function optionNames(options: ListOption[]): (value: unknown) => string {
  const names = new Map(options.map((option) => [option.id, option.name]))
  return (value) => (Array.isArray(value) ? value : [value]).map((id) => names.get(id as number) ?? '').filter(Boolean).join(', ')
}

interface UrgencyDetailPageProps {
  state: UrgencyDetailRouteState
  basePath: string
  /** Opciones del filtro de sexo (el valor del filtro es el `id`). */
  sexes: UrgencySexOptionDTO[]
  /** Códigos de triaje del filtro «Código». */
  priorities: UrgencyPriorityOptionDTO[]
}

export default function UrgencyDetailPage({ state, basePath, sexes, priorities }: UrgencyDetailPageProps) {
  const intl = useIntl()
  const navigate = useNavigate()
  const notify = useNotify()
  // Lo que abrió el detalle: cada criterio se ve como un chip sobre la tabla y se puede quitar
  const [filters, setFilters] = useState(state.filters)

  const [total, setTotal] = useState<number | null>(null)

  // Antes del primer render de la tabla: arranca sin filtros ni orden de una visita anterior
  useState(() => clearPersistedTableState(TABLE_ID))
  useEffect(() => () => clearPersistedTableState(TABLE_ID), [])

  const loadRows = useCallback(async (offSet: number, pageSize: number, tableParams?: unknown) => {
    try {
      return await getUrgencyDetailRows(filters, offSet, pageSize, tableParams, setTotal)
    } catch (error: unknown) {
      if (!isRequestCanceled(error)) notify.error(errorMessageOf(error, intl.formatMessage({ id: 'UrgencySummaryQueryError' })))
      throw error
    }
  }, [filters, intl, notify])

  // Todo lo que acota el detalle es un filtro de la tabla (el usuario lo ve y lo quita ahí), salvo lo que la tabla no puede
  // expresar: «sin financiador» y «sin profesional». Esos son chips aparte. El PDF imprime ambos en el encabezado
  const chips = useMemo<ActiveFilterChip<DetailScope>[]>(() => {
    const message = (id: string, name: string) => intl.formatMessage({ id }, { name })
    const result: ActiveFilterChip<DetailScope>[] = []
    if (filters.noFinancier) {
      result.push({ key: 'financier', label: message('UrgencySummaryContextFinancier', NO_DATA) })
    }
    if (filters.noProfessional) {
      result.push({ key: 'professional', label: message('UrgencySummaryContextProfessional', NO_DATA) })
    }
    return result
  }, [filters.noFinancier, filters.noProfessional, intl])

  // Opciones de las columnas «Estado» y «Código»
  const stateList = useMemo<ListOption[]>(
    () => Object.entries(URGENCY_STATE_GROUP_ID).map(([key, id]) => ({
      id,
      name: intl.formatMessage({ id: URGENCY_STATE_MESSAGE_ID[key as keyof typeof URGENCY_STATE_GROUP_ID] }),
    })),
    [intl],
  )
  const priorityList = useMemo<ListOption[]>(
    () => priorities.map((priority) => ({ id: priority.id, name: readablePriority(priority.name) })),
    [priorities],
  )
  const stateFilter = useMemo(() => multiSelectFilterTemplate({ list: stateList, optionLabel: 'name', optionValue: 'id' }), [stateList])
  const priorityFilter = useMemo(() => multiSelectFilterTemplate({ list: priorityList, optionLabel: 'name', optionValue: 'id' }), [priorityList])

  // Los filtros con los que arranca la tabla: lo que se abrió desde el resumen
  const [defaultFilters] = useState<Record<string, { value: string | number[] }>>(() => {
    const { guardia, financierName, evolutionProfessional, stateGroups, priorityId } = state.seed
    const text = { guardia, financierName, evolutionProfessional }
    return {
      ...Object.fromEntries(
        Object.entries(text)
          .filter((entry): entry is [string, string] => entry[1] !== null)
          .map(([field, value]) => [field, { value }]),
      ),
      ...(stateGroups.length > 0 ? { stateName: { value: stateGroups } } : {}),
      ...(priorityId !== null ? { triageCode: { value: [priorityId] } } : {}),
    }
  })

  const removeScope = useCallback((scope: DetailScope) => {
    setTotal(null)
    setFilters((current) => ({ ...current, ...SCOPE_RESET[scope] }))
  }, [])
  const clearScope = useCallback(() => {
    setTotal(null)
    setFilters((current) => Object.values(SCOPE_RESET).reduce<UrgencyDetailFilters>((acc, reset) => ({ ...acc, ...reset }), current))
  }, [])
  const scopeParts = useMemo(() => chips.map((chip) => chip.label), [chips])

  // Excel y PDF con las mismas columnas, filtros y orden de la tabla; con la casilla «Incluir filtros aplicados» imprimen
  // los chips. El PDF lleva las primeras 2.000 filas y, además de los chips, el contexto del detalle (el período va aparte)
  const exportItems = useMemo<ExportItems>(() => {
    const exportAs = (format: UrgencyExportFormat) =>
      (params: UrgencyTableParams, _columns: unknown, filtersText?: string) => {
        const text = format === 'pdf'
          ? [...scopeParts, filtersText].filter((part): part is string => !!part).join(' · ')
          : filtersText
        return exportUrgencyDetail(filters, params, text, format).catch((error: unknown) => {
          notify.error(errorMessageOf(error, intl.formatMessage({ id: 'UrgencySummaryExportError' })))
          throw error
        })
      }
    return [
      { label: intl.formatMessage({ id: 'UrgencySummaryExportExcel' }), icon: 'pi-file-excel', acceptsFiltersText: true, onExport: exportAs('xlsx') },
      { label: intl.formatMessage({ id: 'UrgencySummaryExportPdf' }), icon: 'pi-file-pdf', acceptsFiltersText: true, onExport: exportAs('pdf') },
    ]
  }, [filters, scopeParts, intl, notify])

  // Espera: filtro de rango (desde / hasta)
  const rangeLabels = useMemo(() => ({
    from: intl.formatMessage({ id: 'FilterRangeFrom' }),
    to: intl.formatMessage({ id: 'FilterRangeTo' }),
  }), [intl])
  const rangeFilter = useMemo(() => numberRangeFilterTemplate(rangeLabels), [rangeLabels])
  // Edad: el mismo rango, solo con enteros (años)
  const ageFilter = useMemo(() => numberRangeFilterTemplate(rangeLabels, { integer: true }), [rangeLabels])
  const yearsUnit = intl.formatMessage({ id: 'UrgencySummaryUnitYears' })

  // Buscador por paciente y filtros por campo. Los textos de la barra ya vienen en español en el framework
  const filterBar = useMemo<CustomTableFilterBar>(() => ({
    searchField: 'patientName',
    searchLabel: intl.formatMessage({ id: 'UrgencySummaryColPatient' }),
    texts: { search: intl.formatMessage({ id: 'UrgencySummaryFilterSearchPatient' }) },
    fields: [
      { field: 'arrivalTime' },
      { field: 'affiliateNumber' },
      { field: 'ageYears', tag: yearsUnit, format: (range: unknown) => formatNumberRange(range, rangeLabels) },
      { field: 'sex', format: optionName(sexes) },
      { field: 'guardia' },
      { field: 'triageCode', format: optionNames(priorityList) },
      { field: 'stateName', format: optionNames(stateList) },
      { field: 'waitMinutes', format: (range: unknown) => formatNumberRange(range, rangeLabels) },
      { field: 'financierName' },
      { field: 'evolutionProfessional' },
      { field: 'notes' },
    ],
  }), [intl, rangeLabels, yearsUnit, sexes, priorityList, stateList])

  const subtitle = intl.formatMessage({ id: 'UrgencySummaryContextRange' }, { from: formatIsoDay(filters.from), to: formatIsoDay(filters.to) })

  function textCell(value: string | null | undefined): ReactElement | string {
    const text = textOrDash(value ?? null)
    // Datos largos: truncados con "…" y el texto completo en el tooltip
    return text === EMPTY_VALUE ? text : <span className="nv-truncate nv-tip" data-pr-tooltip={text}>{text}</span>
  }

  return (
    <div className="nv-scope nv-page nv-urg-detail-page">
      <div className="nv-page-head">
        <Button
          type="button"
          link
          icon="pi pi-angle-double-left"
          label={intl.formatMessage({ id: 'UrgencySummaryBack' })}
          onClick={() => navigate(basePath)}
        />
        <h1 className="nv-h1">{intl.formatMessage({ id: 'UrgencySummaryDetailTitle' })}</h1>
        <p className="nv-subtitle">{subtitle}</p>
        {total !== null && (
          <p className="nv-help" role="status">{intl.formatMessage({ id: 'UrgencySummaryDetailCount' }, { count: total, countText: formatCount(intl, total) })}</p>
        )}
      </div>

      <div className="nv-urg-detail-table">
        <ActiveFilters chips={chips} onRemove={removeScope} onClear={clearScope} />
        <RemoteTable
          // Quitar un chip cambia los criterios fijos: la tabla recarga con los nuevos
          params={filters}
          defaultFilters={defaultFilters}
          defaultsOverwrite
          idTable={TABLE_ID}
          dataKey="rowKey"
          getItems={loadRows}
          filterBar={filterBar}
          refreshButton
          exportItems={exportItems}
          resizableColumns
          tableMinWidth="108rem"
          filtersType={FILTERS_TYPE}
          notFoundText={intl.formatMessage({ id: 'UrgencySummaryDetailEmpty' })}
        >
          <RColumn
            field="arrivalTime"
            header={intl.formatMessage({ id: 'UrgencySummaryColArrival' })}
            grow={1.5}
            filter
            filterElement={DateFilterTemplate}
            body={(row: UrgencyDetailRowDTO) => formatIsoMoment(row.arrivalTime)}
          />
          <RColumn
            field="patientName"
            header={intl.formatMessage({ id: 'UrgencySummaryColPatient' })}
            grow={1.5}
            filter
            filterElement={stringFilterTemplate}
            body={(row: UrgencyDetailRowDTO) => textCell(row.patientName)}
          />
          <RColumn
            field="affiliateNumber"
            header={intl.formatMessage({ id: 'UrgencySummaryColAffiliate' })}
            grow={1.5}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: UrgencyDetailRowDTO) => textCell(row.affiliateNumber)}
          />
          {/* Muestra el texto («8 meses»), pero ordena y filtra por los años enteros */}
          <RColumn
            field="ageYears"
            header={intl.formatMessage({ id: 'UrgencySummaryColAge' })}
            grow={0.8}
            className="nv-urg-center"
            headerClassName="nv-urg-center"
            sortable
            filter
            filterElement={ageFilter}
            body={(row: UrgencyDetailRowDTO) => textOrDash(row.ageText)}
          />
          <RColumn
            field="sex"
            header={intl.formatMessage({ id: 'UrgencySummaryColSex' })}
            grow={1.25}
            sortable
            filter
            filterElement={comboboxFilterTemplate({ list: sexes, optionLabel: 'name', optionValue: 'id' })}
            body={(row: UrgencyDetailRowDTO) => textOrDash(row.sex)}
          />
          <RColumn
            field="guardia"
            header={intl.formatMessage({ id: 'UrgencySummaryTemplate' })}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: UrgencyDetailRowDTO) => textCell(row.guardia)}
          />
          <RColumn
            field="triageCode"
            header={intl.formatMessage({ id: 'UrgencySummaryColCode' })}
            sortable
            filter
            filterElement={priorityFilter}
            body={(row: UrgencyDetailRowDTO) => textCell(row.triageCode)}
          />
          <RColumn
            field="stateName"
            header={intl.formatMessage({ id: 'UrgencySummaryColState' })}
            grow={1.25}
            sortable
            filter
            filterElement={stateFilter}
            body={(row: UrgencyDetailRowDTO) => textCell(row.stateName)}
          />
          <RColumn
            field="waitMinutes"
            header={intl.formatMessage({ id: 'UrgencySummaryColRowWait' })}
            grow={0.75}
            className="nv-urg-center"
            headerClassName="nv-urg-center"
            sortable
            filter
            filterElement={rangeFilter}
            body={(row: UrgencyDetailRowDTO) => formatMinutes(intl, row.waitMinutes)}
          />
          <RColumn
            field="financierName"
            header={intl.formatMessage({ id: 'UrgencySummaryFinancier' })}
            grow={1.25}
            filter
            filterElement={stringFilterTemplate}
            body={(row: UrgencyDetailRowDTO) => textCell(row.financierName)}
          />
          <RColumn
            field="evolutionProfessional"
            header={intl.formatMessage({ id: 'UrgencySummaryColProfessional' })}
            grow={1.5}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: UrgencyDetailRowDTO) => textCell(row.evolutionProfessional)}
          />
          <RColumn
            field="notes"
            header={intl.formatMessage({ id: 'UrgencySummaryColNote' })}
            grow={1.5}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: UrgencyDetailRowDTO) => textCell(row.notes)}
          />
        </RemoteTable>
      </div>
      <NvTooltips />
    </div>
  )
}
